import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Lock,
    RefreshCw,
    ArrowLeft,
    Loader2,
    Inbox,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [passcode, setPasscode] = useState(
        sessionStorage.getItem("sn_admin_pass") || ""
    );
    const [authed, setAuthed] = useState(false);
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async (pass) => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`${API}/admin/submissions`, {
                headers: { "x-admin-passcode": pass },
            });
            setSubs(data);
            setAuthed(true);
        } catch (e) {
            setAuthed(false);
            setError(
                e?.response?.status === 401
                    ? "Incorrect passcode. Please try again."
                    : "Could not reach the server. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const saved = sessionStorage.getItem("sn_admin_pass");
        if (saved) load(saved);
    }, []);

    const unlock = (e) => {
        e.preventDefault();
        sessionStorage.setItem("sn_admin_pass", passcode);
        load(passcode);
    };

    const setStatus = async (id, status) => {
        setSubs((prev) =>
            prev.map((s) => (s.id === id ? { ...s, status } : s))
        );
        try {
            await axios.patch(
                `${API}/admin/submissions/${id}?status=${encodeURIComponent(status)}`,
                {},
                { headers: { "x-admin-passcode": passcode } }
            );
        } catch {
            setError("Status update failed.");
        }
    };

    return (
        <div
            data-testid="admin-submissions-container"
            className="min-h-screen bg-navy-deep text-white"
        >
            <header className="bg-navy/90 backdrop-blur-xl border-b border-gold/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logos/smartnet-reversed-transparent.webp"
                            alt="SMART NET"
                            className="h-8 w-auto"
                        />
                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold hidden sm:inline">
                            Lead inbox
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {authed && (
                            <button
                                data-testid="admin-refresh-button"
                                onClick={() => load(passcode)}
                                className="inline-flex items-center gap-2 border border-white/20 text-mist hover:text-gold hover:border-gold/50 px-4 py-2 rounded-full text-sm transition-colors"
                            >
                                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                                Refresh
                            </button>
                        )}
                        <Link
                            to="/"
                            data-testid="admin-back-to-site-link"
                            className="inline-flex items-center gap-2 text-sm text-mist hover:text-gold transition-colors"
                        >
                            <ArrowLeft size={15} />
                            Back to site
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {!authed ? (
                    <div className="max-w-md mx-auto mt-16">
                        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 text-gold">
                                <Lock size={24} />
                            </div>
                            <h1 className="mt-6 font-display text-2xl font-bold">
                                Admin lead inbox
                            </h1>
                            <p className="mt-2 text-sm text-mist">
                                Enter the admin passcode to view contact form
                                submissions.
                            </p>
                            <form onSubmit={unlock} className="mt-6 space-y-4">
                                <input
                                    data-testid="admin-passcode-input"
                                    type="password"
                                    value={passcode}
                                    onChange={(e) => setPasscode(e.target.value)}
                                    placeholder="Admin passcode"
                                    className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-mist/50 outline-none focus:border-gold transition-colors"
                                />
                                {error && (
                                    <p
                                        data-testid="admin-error-alert"
                                        className="text-red-400 text-sm font-medium"
                                    >
                                        {error}
                                    </p>
                                )}
                                <button
                                    data-testid="admin-unlock-button"
                                    type="submit"
                                    disabled={loading}
                                    className="btn-shine w-full inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold py-3.5 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
                                >
                                    {loading ? (
                                        <Loader2 size={18} className="animate-spin" />
                                    ) : (
                                        <ShieldCheck size={18} />
                                    )}
                                    Unlock inbox
                                </button>
                            </form>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <h1 className="font-display text-3xl font-bold">
                                    Contact submissions
                                </h1>
                                <p className="mt-1 text-sm text-mist">
                                    {subs.length} lead{subs.length === 1 ? "" : "s"} captured
                                </p>
                            </div>
                        </div>

                        {subs.length === 0 ? (
                            <div className="mt-12 bg-white/5 border border-white/10 rounded-3xl p-14 text-center">
                                <Inbox size={40} className="mx-auto text-gold/60" />
                                <p className="mt-5 text-mist">
                                    No submissions yet — leads from the contact
                                    form will appear here in real time.
                                </p>
                            </div>
                        ) : (
                            <div className="mt-8 overflow-x-auto bg-white/5 border border-white/10 rounded-3xl">
                                <table
                                    data-testid="admin-submissions-table"
                                    className="w-full text-sm min-w-[900px]"
                                >
                                    <thead>
                                        <tr className="border-b border-white/10 text-left font-mono text-[11px] uppercase tracking-widest text-gold">
                                            <th className="px-5 py-4">Date</th>
                                            <th className="px-5 py-4">Name</th>
                                            <th className="px-5 py-4">Contact</th>
                                            <th className="px-5 py-4">Company</th>
                                            <th className="px-5 py-4">Services</th>
                                            <th className="px-5 py-4">Budget</th>
                                            <th className="px-5 py-4">Message</th>
                                            <th className="px-5 py-4">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subs.map((s) => (
                                            <tr
                                                key={s.id}
                                                className="border-b border-white/5 hover:bg-white/5 transition-colors align-top"
                                            >
                                                <td className="px-5 py-4 text-mist whitespace-nowrap">
                                                    {new Date(
                                                        s.created_at
                                                    ).toLocaleDateString("en-US", {
                                                        month: "short",
                                                        day: "numeric",
                                                        year: "numeric",
                                                    })}
                                                </td>
                                                <td className="px-5 py-4 font-semibold whitespace-nowrap">
                                                    {s.name}
                                                </td>
                                                <td className="px-5 py-4 text-mist">
                                                    <p>{s.email}</p>
                                                    {s.phone && <p>{s.phone}</p>}
                                                </td>
                                                <td className="px-5 py-4 text-mist">
                                                    {s.company || "—"}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex flex-wrap gap-1.5 max-w-[180px]">
                                                        {(s.services || []).map((sv) => (
                                                            <span
                                                                key={sv}
                                                                className="font-mono text-[10px] uppercase tracking-wider bg-gold/15 text-gold px-2 py-1 rounded-full"
                                                            >
                                                                {sv}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 text-mist whitespace-nowrap">
                                                    {s.budget || "—"}
                                                </td>
                                                <td className="px-5 py-4 text-mist max-w-[240px]">
                                                    {s.message || "—"}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <select
                                                        data-testid={`admin-status-select-${s.id}`}
                                                        value={s.status || "new"}
                                                        onChange={(e) =>
                                                            setStatus(s.id, e.target.value)
                                                        }
                                                        className={`rounded-full px-3 py-1.5 text-xs font-semibold outline-none cursor-pointer ${
                                                            s.status === "new"
                                                                ? "bg-gold/20 text-gold"
                                                                : s.status === "contacted"
                                                                ? "bg-blue-400/20 text-blue-300"
                                                                : s.status === "won"
                                                                ? "bg-emerald-400/20 text-emerald-300"
                                                                : "bg-white/10 text-mist"
                                                        }`}
                                                    >
                                                        <option value="new" className="bg-navy">New</option>
                                                        <option value="contacted" className="bg-navy">Contacted</option>
                                                        <option value="won" className="bg-navy">Won</option>
                                                        <option value="archived" className="bg-navy">Archived</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
